import React, { useState } from "react";
import { CheckCircle, Plus, Minus } from "lucide-react";

import { Link } from "react-router-dom";
import SEO from "../../../components/SEO";

import img11 from "../../../assets/img11.jpeg";
import img12 from "../../../assets/img12.jpg";
import img13 from "../../../assets/img13.webp";
import img14 from "../../../assets/img14.webp";
import img15 from "../../../assets/img15.webp";
import img16 from "../../../assets/img16.webp";
import img17 from "../../../assets/img17.webp";
import BlogSection from "../../../components/BlogSection";

const PermanentLifeInsurance = () => {
  const siteContainer = "mx-auto w-full max-w-[1380px]";
  const [activeIndex, setActiveIndex] = useState(null);
  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const faqs = [
    {
      question: "Qu'est-ce que l'assurance vie permanente au Canada?",
      answer:
        "L'assurance vie permanente au Canada est un plan de protection financière à long terme qui offre une couverture à vie, tant que les primes sont payées. Contrairement à l'assurance vie temporaire, qui expire après une période déterminée, les polices permanentes restent actives toute votre vie et incluent généralement une composante de valeur de rachat qui croît avec le temps. L'assurance vie entière canadienne en est un exemple courant, offrant une couverture garantie, des primes fixes et une accumulation potentielle de capital, ce qui en fait un outil polyvalent pour la planification à long terme.",
    },
    {
      question: "Comment fonctionne l'assurance vie permanente?",
      answer:
        "Une police d'assurance vie permanente combine une protection à vie avec une composante d'épargne. Vous payez des primes régulières, dont une partie finance la couverture d'assurance et une autre s'accumule en valeur de rachat dans la police. Avec le temps, cette valeur de rachat peut croître à impôt différé et être accessible par des retraits ou des avances sur police. À votre décès, l'assureur verse une prestation de décès libre d'impôt à vos bénéficiaires, offrant un soutien financier pour les dettes, la planification successorale et les besoins futurs.",
    },
    {
      question: "L'assurance vie permanente en vaut-elle la peine au Canada?",
      answer:
        "La pertinence de l'assurance vie permanente dépend de vos objectifs financiers. Comme elle offre une couverture à vie et inclut une composante de valeur de rachat, elle peut être précieuse pour les personnes axées sur la planification financière à long terme, la planification successorale ou la transmission d'un héritage. Bien que les primes soient généralement plus élevées que celles de l'assurance temporaire, la protection garantie à vie et l'accumulation potentielle de capital en font un excellent choix pour ceux qui recherchent stabilité et flexibilité au-delà du simple remplacement de revenu.",
    },
    {
      question: "Combien de temps dure l'assurance vie permanente?",
      answer:
        "L'assurance vie permanente au Canada dure toute votre vie, à condition de continuer à payer les primes requises. Contrairement aux polices temporaires qui expirent après un nombre défini d'années, l'assurance vie permanente demeure en vigueur jusqu'à votre décès ou jusqu'à ce que vous choisissiez de résilier la police. Sa couverture à vie garantit que vos bénéficiaires recevront la prestation de décès le moment venu, ce qui peut aider à couvrir des responsabilités financières comme les frais successoraux, les dépenses finales ou un soutien à long terme pour vos proches.",
    },
    {
      question: "Quand devrais-je souscrire une assurance vie permanente?",
      answer:
        "Vous pourriez envisager l'assurance vie permanente si vous souhaitez une couverture à vie et des avantages de planification financière qui vont au-delà d'une protection à court terme. Elle est particulièrement utile pour les personnes ayant des obligations financières à long terme, des besoins de planification successorale, ou celles intéressées à accumuler de la valeur de rachat dans leur police d'assurance. Si vous souhaitez une couverture garantie peu importe les changements futurs de votre santé, ou si vous prévoyez laisser un soutien financier à votre famille après la retraite, l'assurance vie permanente peut être un choix stratégique.",
    },
  ];

  const features = [
    {
      title: "Couverture à vie",
      desc: (
        <>
          Comme mentionné, l'assurance vie permanente offre une couverture pour toute la
          vie de l'assuré, tant que les primes sont payées.
        </>
      ),
    },
    {
      title: "Accumulation de valeur de rachat",
      desc: (
        <>
          L'une des caractéristiques distinctives de l'{" "}
          <span className="text-[#20a7df] font-semibold">
            assurance vie permanente
          </span>{" "}
          est la composante de valeur de rachat. Une partie des paiements de primes va dans
          un compte de valeur de rachat qui s'accumule au fil du temps. Les titulaires de
          police peuvent généralement accéder à cette valeur de rachat par des prêts ou des
          retraits, bien que cela puisse affecter la prestation de décès et entraîner une
          imposition.
        </>
      ),
    },
    {
      title: "Primes",
      desc: (
        <>
          Les primes d'assurance vie permanente sont généralement plus élevées que celles
          de l'assurance vie temporaire. Cependant, une partie de la prime sert à constituer
          une valeur de rachat, contribuant aux avantages financiers à long terme de la
          police.
        </>
      ),
    },
    {
      title: "Prestation de décès",
      desc: (
        <>
          L'assurance vie permanente verse une prestation de décès aux bénéficiaires au
          décès de l'assuré. Ce montant est généralement libre d'impôt sur le revenu.
        </>
      ),
    },
  ];

  return (
    <>
      <SEO
        title="Assurance-vie permanente Canada | Couverture à vie | einsured.ca"
        description="Explorez l’assurance-vie permanente au Canada avec einsured.ca. Profitez d’une couverture à vie et d’une protection financière à long terme."
        keywords="Assurance vie permanente au Canada, assurance vie entière, assurance vie universelle, assurance valeur de rachat"
      />

      {/* HERO SECTION */}
      <section
        className="relative min-h-[60vh] sm:min-h-[70vh] md:min-h-[80vh] bg-cover bg-center flex items-center justify-center md:justify-start px-4 sm:px-6 md:px-20"
        style={{ backgroundImage: `url(${img11})` }}
        role="img"
        aria-label="Bannière principale d'assurance vie permanente"
      >
        <div className="absolute inset-0 bg-black/40"></div>

        <div className={`relative z-10 ${siteContainer}`}>
          <div className="max-w-xl text-center md:text-left text-white">
            <h1 className="text-3xl sm:text-4xl md:text-6xl  font-bold mb-3 sm:mb-4">
              Assurance vie permanente
            </h1>
            <p className="text-base sm:text-lg md:text-xl font-semibold mb-5 sm:mb-6">
              Une couverture qui n'expire jamais avec des primes stables et une sécurité durable pour vous et vos proches
            </p>
            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold px-8 py-3 rounded-full transition"
            >
              Sécuriser maintenant
            </a>
          </div>
        </div>
      </section>

      {/* INTRO & FEATURES */}
      <section className="py-14 sm:py-16 md:py-20 bg-white text-center px-4 sm:px-6 md:px-20">
        <div className={`${siteContainer}`}>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-4 sm:mb-6">
            Assurance vie permanente
          </h2>
          <p className="max-w-4xl mx-auto text-base sm:text-lg text-gray-700 leading-relaxed mb-10 sm:mb-12">
            L'assurance vie permanente est un type d'assurance vie qui offre une couverture
            pour toute la vie du titulaire de la police, tant que les primes sont payées.
            Contrairement à l'{" "}
            <Link
              to="/fr/assurance-vie-temporaire"
              className="text-sky-500 font-semibold hover:underline"
            >
              assurance vie temporaire
            </Link>
            , qui offre une couverture pour une durée déterminée (p. ex. 10, 20 ou 30 ans),
            l'assurance vie permanente n'expire pas tant que la police reste en vigueur,
            c.-à-d. que les primes sont payées ou selon les règles du contrat.
          </p>

          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#49628b] mb-6 sm:mb-10">
            Voici plusieurs caractéristiques clés de l'assurance vie permanente :
          </h3>
          <div className="max-w-5xl mx-auto text-left space-y-5 sm:space-y-6 text-gray-800">
            {features.map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <CheckCircle
                  className="text-sky-500 mt-1 flex-shrink-0"
                  size={22}
                />
                <p className="leading-relaxed text-base sm:text-lg">
                  <span className="font-semibold text-[#1a3d6d]">
                    {item.title} :
                  </span>{" "}
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TERMINOLOGY SECTION */}
      <section
        className="relative py-16 sm:py-20 md:py-28 px-4 sm:px-6 md:px-10 bg-cover bg-center md:bg-fixed"
        style={{ backgroundImage: `url(${img12})` }}
        role="img"
        aria-label="Image de fond pour la terminologie de l'assurance vie permanente"
      >
        <div className="absolute inset-0 bg-sky-900/60"></div>

        <div className={`relative z-10 ${siteContainer} text-white`}>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-3">
            Terminologie de l'assurance vie permanente :
          </h2>
          <p className="text-center max-w-4xl mx-auto mb-6 md:mb-8 leading-relaxed text-gray-100 text-base sm:text-lg">
            Comprendre la terminologie associée à l'assurance vie permanente est essentiel
            pour prendre des décisions éclairées concernant les polices.
          </p>
          <h3 className="text-xl sm:text-2xl md:text-[28px] text-center font-bold mb-8 md:mb-10 text-[#20a7df]">
            Voici les termes clés couramment utilisés dans le contexte de l'assurance vie
            permanente :
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-white/95 rounded-3xl shadow-lg p-6 sm:p-8 leading-relaxed text-gray-800 hover:shadow-xl transition">
              <h4 className="text-[#1a3d6d] text-lg sm:text-xl font-bold text-center mb-3 sm:mb-4">
                Assurance vie permanente :
              </h4>
              <p className="text-base sm:text-lg">
                Une catégorie d'{" "}
                <Link
                  to="/fr/assurance-vie"
                  className="text-sky-600 font-semibold hover:underline"
                >
                  assurance vie
                </Link>{" "}
                qui offre une couverture pour toute la vie de l'assuré, tant que les primes
                sont payées. Elle comprend différents types tels que l'assurance vie entière
                et l'assurance vie universelle.
              </p>
            </div>
            <div className="bg-white/95 rounded-3xl shadow-lg p-6 sm:p-8 leading-relaxed text-gray-800 hover:shadow-xl transition">
              <h4 className="text-[#1a3d6d] text-lg sm:text-xl font-bold text-center mb-3 sm:mb-4">
                Valeur de rachat :
              </h4>
              <p className="text-base sm:text-lg">
                La composante épargne ou placement d'une police d'assurance vie permanente.
                Une partie des paiements de primes est allouée à un compte de valeur de
                rachat, qui s'accumule au fil du temps et peut être accessible au titulaire
                de la police.
              </p>
            </div>

            <div className="bg-white/95 rounded-3xl shadow-lg p-6 sm:p-8 leading-relaxed text-gray-800 hover:shadow-xl transition">
              <h4 className="text-[#1a3d6d] text-lg sm:text-xl font-bold text-center mb-3 sm:mb-4">
                Prestation de décès :
              </h4>
              <p className="text-base sm:text-lg">
                Le montant versé aux bénéficiaires au décès de l'assuré. C'est l'objectif
                principal de l'assurance vie, offrant une protection financière aux membres
                de la famille survivants.
              </p>
            </div>
            <div className="bg-white/95 rounded-3xl shadow-lg p-6 sm:p-8 leading-relaxed text-gray-800 hover:shadow-xl transition">
              <h4 className="text-[#1a3d6d] text-lg sm:text-xl font-bold text-center mb-3 sm:mb-4">
                Primes :
              </h4>
              <p className="text-base sm:text-lg">
                Les paiements périodiques effectués par le titulaire de la police pour
                maintenir la police d'assurance en vigueur. Dans l'assurance vie permanente,
                les primes sont souvent plus élevées que celles de l'assurance vie temporaire
                en raison de la composante valeur de rachat.
              </p>
            </div>

            <div className="bg-white/95 rounded-3xl shadow-lg p-6 sm:p-8 leading-relaxed text-gray-800 hover:shadow-xl transition">
              <h4 className="text-[#1a3d6d] text-lg sm:text-xl font-bold text-center mb-3 sm:mb-4">
                Avance sur police :
              </h4>
              <p className="text-base sm:text-lg">
                Emprunt contre la valeur de rachat d'une police d'assurance vie permanente.
                Le titulaire de la police peut contracter un prêt en utilisant la valeur de
                rachat comme garantie. Cependant, les prêts impayés peuvent réduire la
                prestation de décès et la valeur de rachat.
              </p>
            </div>

            <div className="bg-white/95 rounded-3xl shadow-lg p-6 sm:p-8 leading-relaxed text-gray-800 hover:shadow-xl transition">
              <h4 className="text-[#1a3d6d] text-lg sm:text-xl font-bold text-center mb-3 sm:mb-4">
                Valeur de rachat nette :
              </h4>
              <p className="text-base sm:text-lg">
                La valeur de rachat disponible pour le titulaire de la police s'il choisit
                de racheter ou d'annuler la police d'assurance vie permanente avant son
                décès. Le rachat d'une police peut avoir des implications fiscales.
              </p>
            </div>
            <div className="bg-white/95 rounded-3xl shadow-lg p-6 sm:p-8 md:p-10 leading-relaxed md:col-span-3 text-gray-800 hover:shadow-xl transition">
              <h4 className="text-[#1a3d6d] text-lg sm:text-xl font-bold text-center mb-3 sm:mb-4">
                Dividendes :
              </h4>
              <p className="max-w-5xl mx-auto text-center text-base sm:text-lg">
                Dans le contexte de l'assurance vie entière participante, les dividendes
                représentent une partie des bénéfices de l'assureur distribués aux titulaires
                de police. Les titulaires peuvent utiliser les dividendes pour payer les
                primes, accumuler de la valeur de rachat ou les recevoir en espèces.
              </p>
            </div>
          </div>

          <div className="max-w-5xl mx-auto">
            <p className="mt-8 sm:mt-10 text-gray-100 text-center leading-relaxed text-base sm:text-lg">
              Comprendre ces termes peut aider les individus à naviguer dans les discussions
              avec les professionnels de l'assurance et à prendre des décisions éclairées
              lors du choix d'une police d'assurance vie permanente.
            </p>
            <p className="mt-3 sm:mt-4 text-gray-100 text-center leading-relaxed text-base sm:text-lg">
              Il est conseillé d'examiner attentivement les documents de la police et de
              consulter des conseillers financiers pour s'assurer d'une compréhension
              approfondie des modalités et conditions spécifiques associées à la police
              choisie.
            </p>

            <div className="text-center mt-8 sm:mt-10">
              <a
                href="https://compulife.org/website/655/quoter.html"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold px-8 py-3 rounded-full transition"
              >
                Sécurisez dès aujourd'hui !
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* TYPES OF PERMANENT LIFE INSURANCE */}
      <section className="py-16 sm:py-20 md:py-24 bg-white text-gray-800 px-4 sm:px-6 md:px-16">
        <div className={`${siteContainer}`}>
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-3 sm:mb-4">
              Quels sont les types d'assurance vie permanente ?
            </h2>
            <p className="text-center max-w-4xl mx-auto mb-12 sm:mb-16 text-base sm:text-lg text-gray-600 leading-relaxed">
              Il existe plusieurs types d'assurance vie permanente, chacun avec ses propres
              caractéristiques et avantages. Les principaux types comprennent :
            </p>

            {/* Participating */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10 items-center mb-14 sm:mb-20">
              <div>
                <Link to="/fr/assurance-vie-permanente-participante" className="block">
                  <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3 sm:mb-4 hover:underline cursor-pointer">
                    Assurance vie participante :
                  </h3>
                </Link>
                <ul className="space-y-3 text-gray-700 leading-relaxed text-base sm:text-lg">
                  {[
                    "Également connue sous le nom de « police par », une police d'assurance vie participante offre bien plus qu'une simple prestation de décès.",
                    "Elle vous permet de partager le succès financier de la compagnie d'assurance grâce au versement de dividendes.",
                    "Ces polices sont conçues non seulement pour protéger vos proches, mais aussi pour contribuer à votre croissance financière au fil du temps.",
                  ].map((text, idx) => (
                    <li key={idx} className="flex gap-3">
                      <span className="flex-shrink-0 mt-1.5">
                        <CheckCircle className="text-sky-500" size={20} strokeWidth={2.3} />
                      </span>
                      <span className="flex-1">{text}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="https://compulife.org/website/655/quoter.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-sky-500 hover:bg-sky-600 text-white font-semibold px-8 py-3 rounded-full transition duration-300 shadow-md mt-4"
                >
                  Obtenir une soumission
                </a>
              </div>
              <img
                src={img13}
                alt="Assurance vie participante"
                className="rounded-2xl w-full shadow-md"
              />
            </div>

            {/* Whole */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10 items-center mb-14 sm:mb-20 md:flex-row-reverse">
              <img
                loading="lazy"
                src={img14}
                alt="Assurance vie entière"
                className="rounded-2xl w-full shadow-md md:order-1"
              />
              <div className="md:order-2">
                <Link to="/fr/assurance-vie-entiere" className="block">
                  <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3 sm:mb-4 hover:underline cursor-pointer">
                    Assurance vie entière :
                  </h3>
                </Link>
                <ul className="space-y-3 text-gray-700 leading-relaxed text-base sm:text-lg">
                  {[
                    "Fournit une prestation de décès garantie pour toute la vie de l'assuré.",
                    "Les primes restent fixes pour la durée de vie de la police.",
                    "Constitue une valeur de rachat à un taux garanti et à imposition différée.",
                    "Les titulaires de police peuvent recevoir des dividendes (non garantis) de la compagnie d'assurance, qui peuvent être utilisés pour payer les primes, accumuler une valeur de rachat ou être reçus en espèces.",
                  ].map((text, idx) => (
                    <li key={idx} className="flex gap-3">
                      <span className="flex-shrink-0 mt-1.5">
                        <CheckCircle className="text-sky-500" size={20} strokeWidth={2.3} />
                      </span>
                      <span className="flex-1">{text}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="https://compulife.org/website/655/quoter.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-sky-500 hover:bg-sky-600 text-white font-semibold px-8 py-3 rounded-full transition duration-300 shadow-md mt-4"
                >
                  Obtenir une soumission
                </a>
              </div>
            </div>

            {/* Universal */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10 items-center mb-14 sm:mb-20">
              <div>
                <Link to="/fr/assurance-vie-universelle" className="block">
                  <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3 sm:mb-4 hover:underline cursor-pointer">
                    Assurance vie universelle :
                  </h3>
                </Link>
                <ul className="space-y-3 text-gray-700 leading-relaxed text-base sm:text-lg">
                  {[
                    "Offre une flexibilité dans les paiements de primes et les prestations de décès.",
                    "Les titulaires de police peuvent ajuster le montant et la fréquence des paiements de primes dans certaines limites.",
                    "L'accumulation de valeur de rachat est liée aux taux d'intérêt, et les titulaires de police peuvent avoir la possibilité d'investir dans des comptes séparés.",
                    "Permet des modifications des prestations de décès, sous réserve de certaines conditions.",
                  ].map((text, idx) => (
                    <li key={idx} className="flex gap-3">
                      <span className="flex-shrink-0 mt-1.5">
                        <CheckCircle className="text-sky-500" size={20} strokeWidth={2.3} />
                      </span>
                      <span className="flex-1">{text}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="https://book.einsured.ca/widget/bookings/ei-team"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-sky-500 hover:bg-sky-600 text-white font-semibold px-8 py-3 rounded-full transition duration-300 shadow-md mt-4"
                >
                  Obtenir une soumission
                </a>
              </div>
              <img
                loading="lazy"
                src={img15}
                alt="Assurance vie universelle"
                className="rounded-2xl w-full shadow-md"
              />
            </div>

            {/* Final Expense */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10 items-center mb-14 sm:mb-20 md:flex-row-reverse">
              <img
                loading="lazy"
                src={img16}
                alt="Assurance frais funéraires"
                className="rounded-2xl w-full shadow-md md:order-1"
              />
              <div className="md:order-2">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3 sm:mb-4">
                  Assurance frais funéraires :
                </h3>
                <ul className="space-y-3 text-gray-700 leading-relaxed text-base sm:text-lg">
                  {[
                    "Un type d'assurance vie entière avec une prestation de décès plus faible, conçu pour couvrir les frais funéraires et d'inhumation.",
                    "Généralement plus accessible et abordable que les polices d'assurance vie plus importantes.",
                  ].map((text, idx) => (
                    <li key={idx} className="flex gap-3">
                      <span className="flex-shrink-0 mt-1.5">
                        <CheckCircle className="text-sky-500" size={20} strokeWidth={2.3} />
                      </span>
                      <span className="flex-1">{text}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="https://book.einsured.ca/widget/bookings/ei-team"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-sky-500 hover:bg-sky-600 text-white font-semibold px-8 py-3 rounded-full transition duration-300 shadow-md mt-4"
                >
                  Obtenir une soumission
                </a>
              </div>
            </div>

            {/* Limited Payment */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10 items-center">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3 sm:mb-4">
                  Assurance vie à paiements limités :
                </h3>
                <ul className="space-y-3 text-gray-700 leading-relaxed text-base sm:text-lg">
                  {[
                    "Permet aux titulaires de police de payer des primes pendant un nombre limité d'années (p. ex. 10, 20), après quoi la couverture reste en vigueur pour toute la vie de l'assuré.",
                    "Offre un moyen d'avoir une police libérée après un nombre déterminé de paiements de primes.",
                  ].map((text, idx) => (
                    <li key={idx} className="flex gap-3">
                      <span className="flex-shrink-0 mt-1.5">
                        <CheckCircle className="text-sky-500" size={20} strokeWidth={2.3} />
                      </span>
                      <span className="flex-1">{text}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="https://book.einsured.ca/widget/bookings/ei-team"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-sky-500 hover:bg-sky-600 text-white font-semibold px-8 py-3 rounded-full transition duration-300 shadow-md mt-4"
                >
                  Obtenir une soumission
                </a>
              </div>
              <img
                loading="lazy"
                src={img17}
                alt="Assurance vie à paiements limités"
                className="rounded-2xl w-full shadow-md"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FACTORS AFFECTING PREMIUMS */}
      <section
        id="premium-factors"
        className="relative py-16 sm:py-20 md:py-28 px-4 sm:px-6 md:px-10 bg-gradient-to-br from-[#5f83a3] via-[#5ea6c5] to-[#57b0d6] text-white"
      >
        <div className={`${siteContainer}`}>
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center drop-shadow-sm">
              Facteurs influençant les primes de l'assurance vie permanente :
            </h2>
            <p className="mt-3 sm:mt-4 text-center max-w-4xl mx-auto text-[15px] sm:text-base leading-relaxed opacity-95">
              Plusieurs facteurs influencent les primes de l'assurance vie permanente.
              Comprendre ces facteurs peut aider les individus à évaluer leurs besoins en
              assurance et à déterminer le coût de la couverture.
            </p>
            <h3 className="mt-5 sm:mt-6 md:mt-8 text-xl sm:text-2xl md:text-3xl font-bold text-center drop-shadow-sm">
              Voici les facteurs clés pouvant affecter les primes de l'assurance vie
              permanente :
            </h3>

            <div className="mt-8 sm:mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {[
                {
                  title: "Âge :",
                  text:
                    "L'âge de l'assuré est un facteur important dans la détermination des primes. En général, les personnes plus jeunes ont tendance à payer des primes moins élevées car elles sont considérées comme présentant un risque moindre. À mesure que les individus vieillissent, le risque de mortalité augmente, entraînant des primes plus élevées.",
                },
                {
                  title: "État de santé :",
                  text:
                    "La santé générale de l'assuré est un facteur crucial. Les compagnies d'assurance évaluent la santé par la souscription médicale, en tenant compte de facteurs tels que les conditions médicales préexistantes, les antécédents médicaux familiaux, les choix de style de vie et les résultats d'un examen médical. Les individus en bonne santé bénéficient généralement de primes moins élevées.",
                },
                {
                  title: "Sexe :",
                  text:
                    "Statistiquement, les femmes vivent plus longtemps que les hommes, elles paient donc souvent des primes moins élevées pour la même couverture.",
                },
                {
                  title: "Profession et style de vie :",
                  text:
                    "Certaines professions et certains styles de vie peuvent être considérés comme plus risqués par les compagnies d'assurance, entraînant des primes plus élevées. Les emplois présentant des risques physiques élevés ou des conditions dangereuses peuvent entraîner des coûts accrus. De même, la pratique d'activités telles que les sports extrêmes ou certains loisirs peut avoir une incidence sur les taux de prime.",
                },
                {
                  title: "Statut de fumeur :",
                  text:
                    "Le tabagisme est un facteur de risque important pour diverses conditions de santé, de sorte que les fumeurs paient généralement des primes plus élevées que les non-fumeurs. Certaines compagnies d'assurance peuvent faire la distinction entre différentes formes d'usage du tabac, telles que les cigarettes, les cigares ou le tabac sans fumée.",
                },
                {
                  title: "Montant de la couverture :",
                  text:
                    "Le montant de la prestation de décès, ou montant de la couverture, choisi par le titulaire de la police affecte directement les primes. Des montants de couverture plus élevés entraînent des primes plus élevées. Les individus doivent évaluer soigneusement leurs besoins en assurance et choisir une prestation de décès appropriée.",
                },
                {
                  title: "Type de police :",
                  text:
                    "Les différents types d'assurance vie permanente ont des structures de primes variées. L'assurance vie entière a souvent des primes fixes pour la durée de vie de la police, tandis que l'assurance vie universelle et l'assurance vie variable peuvent offrir plus de flexibilité mais peuvent également avoir des primes variables selon les conditions du marché et la performance de la police.",
                },
                {
                  title: "Structure de paiement :",
                  text:
                    "La façon dont les primes sont payées peut avoir une incidence sur le coût total. Les polices à prime unique impliquent un paiement forfaitaire, tandis que les primes régulières peuvent être payées annuellement, semestriellement, trimestriellement ou mensuellement. Payer des primes plus fréquemment peut entraîner des coûts totaux légèrement plus élevés.",
                },
                {
                  title: "Avenants et caractéristiques supplémentaires :",
                  text:
                    "Les titulaires de police peuvent ajouter des avenants ou des caractéristiques supplémentaires à leurs polices d'assurance pour une couverture supplémentaire. Cependant, ces ajouts peuvent augmenter les primes. Les avenants courants comprennent les avenants de prestation de décès accélérée, les avenants de soins de longue durée et les avenants de revenu d'invalidité.",
                },
                {
                  title: "Composante valeur de rachat :",
                  text:
                    "Les polices d'assurance vie permanente, notamment l'assurance vie entière et l'assurance vie universelle, ont une composante de valeur de rachat. Le montant alloué à la valeur de rachat peut affecter les primes. Les polices avec une accumulation de valeur de rachat plus élevée peuvent avoir des primes plus élevées.",
                },
                {
                  title: "Compagnie d'assurance et souscription :",
                  text:
                    "Différentes compagnies d'assurance ont des critères de souscription et des structures de prix variés. Il est essentiel de magasiner et de comparer les soumissions de plusieurs assureurs pour trouver les tarifs les plus compétitifs.",
                },
              ].map((card, i) => (
                <div
                  key={i}
                  className="rounded-3xl bg-[rgba(51,93,117,0.9)] border border-white/10 shadow-[0_15px_35px_rgba(0,0,0,0.25)] p-6 sm:p-7 md:p-8"
                >
                  <h4 className="text-lg sm:text-xl font-bold text-center mb-3">
                    {card.title}
                  </h4>
                  <div className="flex gap-3">
                    <CheckCircle className="h-5 w-5 text-cyan-300 mt-1 flex-shrink-0" />
                    <p className="leading-relaxed text-[15px] sm:text-base">
                      {card.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-center mt-8 sm:mt-10">
              <button
                onClick={() =>
                  window.open(
                    "https://book.einsured.ca/widget/bookings/ei-team",
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
                className="w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold px-8 py-3 rounded-full transition"
              >
                Obtenez les meilleurs tarifs
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* DIFFERENCE: PERMANENT vs TERM */}
      <section
        id="permanent-vs-term"
        className="py-14 sm:py-16 md:py-20 px-4 sm:px-6 md:px-10 bg-white"
      >
        <div className={`${siteContainer}`}>
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center">
              Différence entre l'assurance vie permanente et l'assurance vie temporaire :
            </h2>
            <p className="text-center text-gray-600 mt-3 mb-6 md:mb-10 text-[15px] sm:text-base">
              Raisons pour lesquelles les individus considèrent l'assurance vie permanente
              préférable à l'assurance vie temporaire.
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-300 text-gray-700 border-collapse text-sm md:text-base">
                <thead>
                  <tr className="bg-gray-50/70">
                    <th className="text-center px-4 md:px-5 py-3 md:py-4 font-semibold text-slate-700 border border-gray-300 w-1/4">
                      Critère
                    </th>
                    <th className="text-left px-4 md:px-5 py-3 md:py-4 font-semibold text-slate-700 border border-gray-300">
                      Assurance vie permanente
                    </th>
                    <th className="text-left px-4 md:px-5 py-3 md:py-4 font-semibold text-slate-700 border border-gray-300">
                      Assurance vie temporaire
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      c: "Durée de couverture",
                      p: (
                        <>
                          Offre une couverture pour{" "}
                          <span className="underline">toute la vie</span> de l'assuré.
                        </>
                      ),
                      t: (
                        <>
                          Offre une couverture pour une{" "}
                          <span className="underline">durée déterminée</span> (p. ex. 10, 20,
                          30 ans).
                        </>
                      ),
                    },
                    {
                      c: "Valeur de rachat",
                      p: (
                        <>
                          <span className="underline">Constitue une valeur de rachat</span>{" "}
                          au fil du temps, permettant aux titulaires d'accéder aux fonds par
                          des prêts ou des retraits.
                        </>
                      ),
                      t: (
                        <>
                          <span className="underline">N'a pas de composante valeur de rachat</span>{" "}
                          ; les primes contribuent uniquement à la prestation de décès.
                        </>
                      ),
                    },
                    {
                      c: "Planification financière",
                      p: (
                        <>
                          Sert d'{" "}
                          <span className="underline">
                            outil de planification financière à long terme
                          </span>
                          , combinant l'assurance vie avec une composante épargne ou
                          placement.
                        </>
                      ),
                      t: (
                        <>
                          Se concentre principalement sur la prestation d'une prestation de
                          décès pendant la durée ;{" "}
                          <span className="underline">
                            fonctionnalités de planification financière limitées
                          </span>
                          .
                        </>
                      ),
                    },
                    {
                      c: "Primes",
                      p: (
                        <>
                          Les polices d'assurance vie entière comportent généralement des{" "}
                          <span className="underline">primes fixes</span> pour la durée de
                          vie de la police, offrant une prévisibilité.
                        </>
                      ),
                      t: (
                        <>
                          Les primes{" "}
                          <span className="underline">peuvent augmenter</span>{" "}
                          considérablement lors du renouvellement des polices temporaires
                          après l'expiration du terme initial.
                        </>
                      ),
                    },
                    {
                      c: "Planification successorale",
                      p: (
                        <>
                          Peut être utilisée pour la{" "}
                          <span className="underline">planification successorale</span>,
                          fournissant une prestation de décès non imposable pour couvrir les
                          droits de succession ou laisser un héritage.
                        </>
                      ),
                      t: (
                        <>
                          L'assurance temporaire{" "}
                          <span className="underline">peut ne pas être aussi efficace</span>{" "}
                          pour les besoins de planification successorale complexes.
                        </>
                      ),
                    },
                    {
                      c: "Accès aux fonds",
                      p: (
                        <>
                          Les titulaires peuvent{" "}
                          <span className="underline">accéder à la valeur de rachat</span>{" "}
                          pour divers besoins sans racheter entièrement la police.
                        </>
                      ),
                      t: (
                        <>
                          <span className="underline">
                            Aucun accès à la valeur de rachat accumulée
                          </span>{" "}
                          pendant la durée ; les primes contribuent uniquement à la
                          prestation de décès.
                        </>
                      ),
                    },
                    {
                      c: "Accessibilité à long terme",
                      p: (
                        <>
                          Bien que les primes soient plus élevées, le titulaire peut trouver
                          une{" "}
                          <span className="underline">accessibilité à long terme</span>{" "}
                          grâce à la composante valeur de rachat.
                        </>
                      ),
                      t: (
                        <>
                          Généralement plus abordable à court terme, mais{" "}
                          <span className="underline">
                            les primes peuvent augmenter considérablement si renouvelées
                          </span>{" "}
                          après le terme initial.
                        </>
                      ),
                    },
                  ].map((row, i) => (
                    <tr key={i} className="align-top">
                      <th className="align-top px-4 md:px-5 py-4 md:py-5 text-slate-700 font-semibold border border-gray-300">
                        {row.c}
                      </th>
                      <td className="align-top px-4 md:px-5 py-4 md:py-5 border border-gray-300">
                        {row.p}
                      </td>
                      <td className="align-top px-4 md:px-5 py-4 md:py-5 border border-gray-300">
                        {row.t}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-center mt-8 md:mt-10">
              <button
                onClick={() =>
                  window.open(
                    "https://compulife.org/website/655/quoter.html",
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
                className="w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold px-8 py-3 rounded-full transition"
              >
                Commencer
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* WHY BUY FROM EINSURED */}
      <section
        id="why-einsured"
        className="relative bg-[#e3e9f1] py-14 sm:py-16 md:py-20 px-4 sm:px-6 md:px-10
               before:content-[''] before:absolute before:left-0 before:right-0 before:top-0 before:h-[3px] before:bg-[#4a6a94]
               after:content-[''] after:absolute after:left-0 after:right-0 after:bottom-0 after:h-[3px] after:bg-[#4a6a94]"
      >
        <div className={`${siteContainer}`}>
          <div className="max-w-5xl mx-auto text-slate-700">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-[#49628b]">
              Pourquoi acheter une assurance vie permanente chez einsured.ca ?
            </h2>

            <p className="mt-4 text-center leading-relaxed text-base sm:text-lg">
              Chez{" "}
              <Link to="/" className="text-[#49628b] underline font-semibold">
                einsured.ca
              </Link>
              , nous comprenons que sécuriser votre avenir financier est une priorité
              absolue. En ce qui concerne l'assurance vie permanente, nos solutions de
              courtage se distinguent comme le meilleur choix pour les personnes cherchant
              une couverture complète, une flexibilité financière et la tranquillité d'esprit.
            </p>

            <div className="mt-8 sm:mt-10 space-y-6 sm:space-y-7">
              {[
                {
                  h: "1. Couverture adaptée à vos besoins :",
                  t: (
                    <>
                      Notre équipe expérimentée chez{" "}
                      <Link
                        to="/"
                        className="text-[#20a7df] underline font-semibold"
                      >
                        einsured.ca
                      </Link>{" "}
                      adopte une approche personnalisée pour comprendre vos objectifs
                      financiers uniques et votre situation de vie. Nous travaillons en
                      étroite collaboration avec{" "}
                      <span className="text-[#20a7df] underline">des fournisseurs de premier plan</span>{" "}
                      pour élaborer des solutions d'assurance vie permanente qui correspondent
                      à vos besoins, en vous assurant de recevoir la couverture qui compte le
                      plus pour vous et vos proches.
                    </>
                  ),
                },
                {
                  h: "2. Gamme complète d'options d'assurance vie permanente :",
                  t: (
                    <>
                      Nous reconnaissons qu'une solution unique ne convient pas à tous.
                      C'est pourquoi einsured.ca offre une gamme diversifiée d'options
                      d'assurance vie permanente, notamment l'assurance vie entière, l'assurance
                      vie universelle et plus encore. Que vous recherchiez une couverture
                      garantie, des primes flexibles ou des opportunités de placement, nous
                      avons la bonne solution pour correspondre à vos préférences.
                    </>
                  ),
                },
                {
                  h: "3. Primes compétitives et solutions rentables :",
                  t: (
                    <>
                      L'accessibilité financière est une préoccupation clé pour nos clients,
                      et nous priorisons des solutions rentables sans compromettre la qualité.
                      Nos services de courtage nous permettent de comparer les soumissions des
                      principaux fournisseurs d'assurance, en vous assurant de recevoir les
                      primes les plus compétitives pour la couverture dont vous avez besoin.
                      Nous croyons en l'accessibilité de l'assurance vie permanente pour tous
                      ceux qui valorisent la sécurité financière à long terme.
                    </>
                  ),
                },
                {
                  h: "4. Conseils d'experts et avis transparent :",
                  t: (
                    <>
                      Naviguer dans les complexités de l'assurance vie permanente peut être
                      accablant, mais avec{" "}
                      <Link
                        to="/"
                        className="text-[#20a7df] underline font-semibold"
                      >
                        einsured.ca
                      </Link>
                      , vous n'êtes jamais seul. Notre équipe de professionnels de l'assurance
                      compétents vous fournit des conseils d'experts, répond à vos questions
                      et offre des avis transparents pour vous aider à prendre des décisions
                      éclairées concernant votre avenir financier. Nous nous engageons à
                      établir des relations durables fondées sur la confiance et l'intégrité.
                    </>
                  ),
                },
                {
                  h: "5. Technologie innovante pour des processus sans tracas :",
                  t: (
                    <>
                      Chez{" "}
                      <Link
                        to="/"
                        className="text-[#20a7df] underline font-semibold"
                      >
                        einsured.ca
                      </Link>
                      , nous exploitons une technologie de pointe pour simplifier le processus
                      d'assurance pour nos clients. De l'obtention de soumissions à la
                      complétion des demandes, notre plateforme conviviale garantit une
                      expérience sans tracas. Nous priorisons l'efficacité, vous permettant
                      de vous concentrer sur ce qui compte le plus pendant que nous nous
                      occupons des détails.
                    </>
                  ),
                },
                {
                  h: "6. Engagement envers la satisfaction client :",
                  t: (
                    <>
                      Notre engagement envers la satisfaction client est au cœur de tout ce
                      que nous faisons. Nous priorisons une communication claire, des réponses
                      rapides et un soutien continu tout au long de la durée de vie de votre
                      police. Votre tranquillité d'esprit est notre priorité, et nous nous
                      dédions à dépasser vos attentes à chaque étape.
                    </>
                  ),
                },
              ].map((box, i) => (
                <div
                  key={i}
                  className="bg-white rounded-[28px] border border-slate-300 shadow-[0_10px_30px_rgba(20,35,60,0.15)] p-6 md:p-8"
                >
                  <h4 className="text-[#49628b] font-bold text-base sm:text-lg md:text-xl mb-2">
                    {box.h}
                  </h4>
                  <p className="leading-relaxed text-base sm:text-lg">{box.t}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 md:mt-10 text-center">
              <p className="leading-relaxed text-base sm:text-lg">
                Choisissez{" "}
                <Link to="/" className="text-[#20a7df] underline font-semibold">
                  einsured.ca
                </Link>{" "}
                pour vos besoins en assurance vie permanente et entamez un parcours vers la
                sécurité financière et la tranquillité d'esprit. Laissez notre expertise,
                notre approche personnalisée et notre engagement envers l'excellence vous
                guider sur la voie d'un avenir sûr et prospère.
              </p>
              <p className="mt-3 sm:mt-4 leading-relaxed text-base sm:text-lg">
                <Link to="/fr/contactez-nous" className="underline text-[#20a7df]">
                  Contactez-nous aujourd'hui
                </Link>{" "}
                pour explorer les meilleures solutions d'{" "}
                <span className="text-[#20a7df] font-semibold">assurance vie permanente</span>{" "}
                adaptées spécialement pour vous.
              </p>
              <p className="text-[#20a7df] mt-3 font-medium text-slate-800">
                Votre bien-être financier est notre affaire.
              </p>

              <div className="mt-6 sm:mt-8 flex justify-center">
                <button
                  onClick={() =>
                    window.open(
                      "https://compulife.org/website/655/quoter.html",
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                  className="w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold px-8 py-3 rounded-full transition"
                >
                  Commencer
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Frequently Asked Questions */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 md:px-24 bg-white text-gray-800">
        <div className={`${siteContainer}`}>
          <div className="max-w-5xl mx-auto text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#49628b]">
              Foire aux questions sur l'assurance vie permanente
            </h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => {
              const expanded = activeIndex === index;
              const panelId = `faq-panel-${index}`;
              const btnId = `faq-btn-${index}`;
              return (
                <div
                  key={index}
                  className="border rounded-lg shadow-sm transition-all bg-white"
                >
                  <button
                    id={btnId}
                    className="w-full flex justify-between items-center text-left px-4 sm:px-6 py-3.5 sm:py-4 font-semibold text-base sm:text-lg text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1e48a0] focus-visible:ring-offset-2"
                    onClick={() => toggleFAQ(index)}
                    aria-expanded={expanded}
                    aria-controls={panelId}
                  >
                    {faq.question}
                    {expanded ? (
                      <Minus
                        className="w-6 h-6 text-gray-700"
                        aria-hidden="true"
                      />
                    ) : (
                      <Plus
                        className="w-6 h-6 text-gray-700"
                        aria-hidden="true"
                      />
                    )}
                  </button>

                  {expanded && (
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={btnId}
                      className="px-4 sm:px-6 pb-4 text-gray-700 text-sm sm:text-base"
                    >
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <BlogSection title="Derniers articles" mode="related" relatedCount={3} />
    </>
  );
};

export default PermanentLifeInsurance;
